import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import NewReport from '../NewReport';
import ReportDetails from './ReportDetails';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { faXmark, faPlus } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { ReportAPIResponse, getReportsApi } from '../api';
import { useClient } from 'shared/apollo';
import { Program } from '../../Reports/NewReportDialog';
import { TrueImpactTransparentButton } from 'shared/design-system/components/TrueImpactButton';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import ProgramQuestionsModal from '../ProgramQuestions/ProgramQuestionsModal';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

export interface ReportColumnProps {
  programName: string;
  program: Program;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reloadProgramList: () => unknown;
  onClose: () => unknown;
}

const StyledDiv = styled.div`
  flex: 1;
  overflow: auto;
  ${thinScrollbarStyles}
  padding: 36px ${SPACERS.large} 0 ${SPACERS.extraLarge};
  .report-details-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .report-details-header-heading {
      margin-bottom: 0;
    }
    .report-details-header-buttons {
      display: flex;
      gap: ${SPACERS.small};
    }
  }
  .profiling-callout {
    margin-top: ${SPACERS.large};
    padding: ${SPACERS.large};
    background-color: ${COLORS.warningBackground};
    border: 1px solid ${COLORS.warningSubdued};
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.small};
    align-items: flex-start;
  }
`;

const ReportColumn: React.FC<ReportColumnProps> = ({
  programName,
  program,
  programsList,
  reloadProgramList,
  onClose,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [existingYear, setExistingYear] = useState<number | undefined>(
    undefined,
  );
  const [showProfiling, setShowProfiling] = useState<boolean>(false);
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<{
    reportsTodo: ReportAPIResponse[];
  }>();

  const programProfilingEnabled = useFeatureFlags('programProfiling');
  const canCreateReport =
    !programProfilingEnabled || Boolean(program.isCompleted);

  const cloneExistingYear = useCallback(
    (year: number) => {
      setOpen(true);
      setExistingYear(year);
    },
    [setExistingYear, setOpen],
  );
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
    setExistingYear(undefined);
  }, [setOpen, setExistingYear]);

  const handleProfilingOpen = useCallback(() => setShowProfiling(true), []);
  const handleProfilingClose = useCallback(() => {
    setShowProfiling(false);
    reloadProgramList();
  }, [reloadProgramList]);

  useEffect(() => {
    if (!client) return;
    makeRequest(
      getReportsApi(client, {
        searchText: '',
        filter: 'Program',
        quickFilter: 'All',
        first: undefined,
        offset: undefined,
        groupFilter: [{ program: programName }],
      }),
    );
  }, [client]);

  return (
    <StyledDiv>
      {open && canCreateReport && (
        <NewReport
          open={true}
          onClose={handleClose}
          programsList={programsList}
          existingProgram={program}
          existingYear={existingYear}
          handleReportCountLoad={undefined}
        />
      )}
      <ProgramQuestionsModal
        open={showProfiling}
        programId={program.Id}
        onClose={handleProfilingClose}
      />
      <div className="report-details-header">
        <TrueImpactText
          variant="heading-subheading"
          className="report-details-header-heading"
        >
          Annual Social Impact Reports
        </TrueImpactText>
        <div className="report-details-header-buttons">
          {canCreateReport && (
            <TrueImpactTransparentButton onClick={handleOpen} icon={faPlus}>
              New Report
            </TrueImpactTransparentButton>
          )}
          <TrueImpactIconButton
            icon={faXmark}
            label="Close"
            onClick={onClose}
          />
        </div>
      </div>
      {!canCreateReport ? (
        <div className="profiling-callout">
          <TrueImpactText variant="body-bold">
            Complete Program Profiles to create reports
          </TrueImpactText>
          <TrueImpactText variant="body-small">
            You need to finish answering this program&apos;s profiling questions
            before you can create new reports or clone previous years.
          </TrueImpactText>
          <TrueImpactButton onClick={handleProfilingOpen}>
            Complete Program Profiles
          </TrueImpactButton>
        </div>
      ) : requestState.type === 'REQUEST_SUCCESS' ? (
        <ReportDetails
          program={program}
          cloneExistingYear={cloneExistingYear}
          reports={requestState.data.reportsTodo}
        />
      ) : (
        <TrueImpactLoading />
      )}
    </StyledDiv>
  );
};

export default ReportColumn;
